/**
 * How content generation names its score rows: `overall` is the headline, the five rubric
 * dimensions are fixed, `_`-prefixed names are reserved blobs, and every other name is a
 * product field (its enrichment field code). Mirrors catalog-service `score_read.py`.
 */
export const HEADLINE_SCORE = 'overall';
export const SCORE_DIMENSIONS = [
  'correctness',
  'completeness',
  'compliance',
  'relevance',
  'differentiation',
] as const;

const DIMENSION_SET = new Set<string>(SCORE_DIMENSIONS);

/** True for a per-field score name (not the headline, a dimension, or a reserved row). */
export function isFieldScore(name: string): boolean {
  return name !== HEADLINE_SCORE && !name.startsWith('_') && !DIMENSION_SET.has(name);
}

/** True for one of the five rubric dimensions. */
export function isDimensionScore(name: string): boolean {
  return DIMENSION_SET.has(name);
}

/** Field scores of a `scores` record, weakest first — what "Weakest fields" reads. */
export function fieldScoresAscending(
  scores: Record<string, number | null>,
): { name: string; value: number }[] {
  return Object.entries(scores)
    .filter((e): e is [string, number] => isFieldScore(e[0]) && typeof e[1] === 'number')
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => a.value - b.value);
}

/**
 * Score bands every score bar and number shares (round 5): ≥95 good, 85–94 needs a look,
 * below 85 a problem. Content generation sends only a per-product tier and one scorecard
 * `belowThreshold`, never per-score bands, so the bands live here.
 */
export const SCORE_BAND = { good: 95, fair: 85 } as const;

/** Tone of one score on its scale (`max` 100 for content-gen, 10 for the self-grade). */
export function scoreBandTone(value: number, max = 100): 'success' | 'waiting' | 'failed' {
  const pct = max > 0 ? (value / max) * 100 : value;
  if (pct >= SCORE_BAND.good) return 'success';
  if (pct >= SCORE_BAND.fair) return 'waiting';
  return 'failed';
}

/** Words that read as acronyms in a field name (G3: "aeo" → "AEO", never "Aeo"). */
const ACRONYMS = new Set([
  'aeo',
  'geo',
  'seo',
  'gtin',
  'mpn',
  'sku',
  'id',
  'url',
  'faq',
  'ai',
  'http',
  'https',
  'uk',
  'us',
]);

/**
 * A score / field code as words (G3): `google_product_category` → "Google product category",
 * `aeo` → "AEO". The fallback behind the locale's `scoreDetails.field.*` map.
 */
export function humanizeFieldName(name: string): string {
  const words = name
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase())
    .map((w, i) =>
      ACRONYMS.has(w) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w,
    );
  return words.join(' ');
}

/**
 * A check id's rule part as words — `description.states_facts` → "States facts",
 * `image_link.https` → "HTTPS" — for a check list already grouped under its field. Never the raw
 * dotted id: only the last segment is humanised.
 */
export function checkRuleLabel(checkId: string): string {
  const rule = checkId.slice(checkId.lastIndexOf('.') + 1);
  return humanizeFieldName(rule || checkId.replace(/\./g, ' '));
}

/** A check id's field part as words (`image_link.present` → "Image link"); '' when it has none. */
export function checkFieldLabel(checkId: string): string {
  const dot = checkId.lastIndexOf('.');
  return dot > 0 ? humanizeFieldName(checkId.slice(0, dot)) : '';
}
